import React, { useState } from 'react';
import { MdClose, MdFileUpload, MdDownload, MdCheckCircle, MdError } from 'react-icons/md';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';

const ExcelImportModal = ({
  isOpen,
  onClose,
  type = 'officers', // 'officers' or 'traders'
  onImport,
  userMmdaId, // Add userMmdaId prop
  user, // Add user prop
  fetchData // Add fetchData prop to refresh data after import
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percentage: 0 });

  if (!isOpen) return null;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    setUploadProgress({ current: 0, total: 0, percentage: 0 });

    try {
      // Read the Excel/CSV file
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        throw new Error('File is empty or has no valid data');
      }

      const results = {
        successful: 0,
        errors: [],
        total: jsonData.length
      };

      // Initialize progress
      setUploadProgress({
        current: 0,
        total: jsonData.length,
        percentage: 0
      });

      // Process each row
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];

        // Skip completely empty rows
        const hasData = Object.values(row).some(value =>
          value !== null && value !== undefined && String(value).trim() !== ''
        );

        if (!hasData) {
          console.log(`Skipping empty row ${i + 1}`);
          continue;
        }

        try {
          if (type === 'traders') {
            await processTraderRow(row, i + 1);
          } else {
            await processOfficerRow(row, i + 1);
          }
          results.successful++;
        } catch (error) {
          console.error(`Error processing row ${i + 1}:`, error);
          results.errors.push({
            row: i + 1,
            data: row,
            error: error.message
          });
        }

        // Update progress
        const current = i + 1;
        const percentage = Math.round((current / jsonData.length) * 100);
        setUploadProgress({
          current,
          total: jsonData.length,
          percentage
        });

        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const result = {
        success: results.errors.length < jsonData.length,
        imported: results.successful,
        errors: results.errors.length,
        errorDetails: results.errors,
        message: `Import completed: ${results.successful} successful, ${results.errors.length} errors`
      };

      setImportResult(result);
      if (onImport) onImport(result);
      if (fetchData) fetchData(); // Refresh the data
    } catch (error) {
      console.error('Import error:', error);
      setImportResult({
        success: false,
        message: 'Import failed. Please check your file format.',
        error: error.message
      });
    } finally {
      setImporting(false);
    }
  };

  const processTraderRow = async (row, rowNumber) => {
    // Helper function to safely convert to string and trim
    const safeString = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };

    // Debug: Log the raw row data
    console.log(`Processing row ${rowNumber}:`, row);

    // Map Excel columns to expected fields
    const traderData = {
      business_name: safeString(row['Business Name'] || row['business_name']),
      owner_name: safeString(row['Owner Name'] || row['owner_name']),
      phone: safeString(row['Phone'] || row['phone']),
      email: safeString(row['Email'] || row['email']) || null,
      business_type: safeString(row['Business Type'] || row['business_type']) || 'Retail',
      location: safeString(row['Location'] || row['location']),
      mmda_id: userMmdaId,
      zone_id: null, // Will be set based on zone name if provided
      group_id: null, // Will be set based on group name if provided
      status: 'active',
      created_by: user?.user_id,
      registration_date: new Date().toISOString().split('T')[0]
    };

    // Debug: Log the processed data
    console.log(`Processed trader data for row ${rowNumber}:`, traderData);

    // Validate required fields
    if (!traderData.business_name) {
      throw new Error('Business Name is required');
    }
    if (!traderData.owner_name) {
      throw new Error('Owner Name is required');
    }
    if (!traderData.phone) {
      throw new Error('Phone is required');
    }
    if (!traderData.location) {
      throw new Error('Location is required');
    }

    // Split owner_name into first_name and last_name for compatibility
    const nameParts = traderData.owner_name.split(' ');
    traderData.first_name = nameParts[0] || 'Unknown';
    traderData.last_name = nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'Owner';

    // Create the trader
    try {
      console.log(`Sending trader data to API:`, traderData);
      const response = await axios.post(`${API_BASE_URL}/traders`, traderData, API_CONFIG);
      console.log(`API response for row ${rowNumber}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`API error for row ${rowNumber}:`, error.response?.data || error.message);
      throw new Error(error.response?.data?.message || error.message || 'Failed to create trader');
    }
  };

  const processOfficerRow = async (row, rowNumber) => {
    // Helper function to safely convert to string and trim
    const safeString = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };

    // Map Excel columns to expected fields
    const officerData = {
      first_name: safeString(row['First Name'] || row['first_name']),
      last_name: safeString(row['Last Name'] || row['last_name']),
      email: safeString(row['Email'] || row['email']),
      phone: safeString(row['Phone'] || row['phone']),
      mmda_id: userMmdaId,
      zone_id: null, // Will be set based on zone name if provided
      status: safeString(row['Status'] || row['status']) || 'active',
      created_by: user?.user_id
    };

    // Validate required fields
    if (!officerData.first_name) {
      throw new Error('First Name is required');
    }
    if (!officerData.last_name) {
      throw new Error('Last Name is required');
    }
    if (!officerData.email) {
      throw new Error('Email is required');
    }
    if (!officerData.phone) {
      throw new Error('Phone is required');
    }

    // Create the officer
    const response = await axios.post(`${API_BASE_URL}/revenue-officers`, officerData, API_CONFIG);
    return response.data;
  };

  const downloadTemplate = () => {
    const templateData = type === 'officers'
      ? [
          ['First Name', 'Last Name', 'Email', 'Phone', 'Status'],
          ['John', 'Doe', 'john@example.com', '0241234567', 'active'],
          ['Jane', 'Smith', 'jane@example.com', '0241234568', 'active']
        ]
      : [
          ['Business Name', 'Owner Name', 'Phone', 'Email', 'Business Type', 'Location'],
          ['ABC Store', 'John Doe', '0241234567', 'john@abc.com', 'Retail', 'Main Street, Accra'],
          ['XYZ Shop', 'Jane Smith', '0241234568', 'jane@xyz.com', 'Wholesale', 'Market Square, Kumasi']
        ];

    const csvContent = templateData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetModal = () => {
    setSelectedFile(null);
    setImporting(false);
    setImportResult(null);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
        width: '90%',
        maxWidth: 600,
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid #e0e0e0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdFileUpload size={24} style={{ color: '#17a2b8' }} />
            <h2 style={{ margin: 0, color: '#333', fontSize: 20 }}>
              Import {type === 'officers' ? 'Revenue Officers' : 'Traders'}
            </h2>
          </div>
          <button
            onClick={resetModal}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4,
            }}
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {!importResult ? (
          <>
            {/* Instructions */}
            <div style={{
              backgroundColor: '#d1ecf1',
              border: '1px solid #bee5eb',
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
            }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#0c5460', fontSize: 14 }}>
                Import Instructions:
              </h3>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#0c5460', fontSize: 12 }}>
                <li>Download the template file to see the required format</li>
                <li>Fill in your data following the exact column headers</li>
                <li>Save as Excel (.xlsx) or CSV (.csv) format</li>
                <li>Upload the file using the button below</li>
              </ul>
            </div>

            {/* Download Template */}
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={downloadTemplate}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                <MdDownload size={18} />
                Download Template File
              </button>
            </div>

            {/* File Upload */}
            <div style={{
              border: '2px dashed #dee2e6',
              borderRadius: 8,
              padding: 40,
              textAlign: 'center',
              marginBottom: 20,
              backgroundColor: selectedFile ? '#d4edda' : '#f8f9fa',
            }}>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                style={{
                  cursor: 'pointer',
                  display: 'block',
                }}
              >
                <MdFileUpload size={48} style={{ 
                  color: selectedFile ? '#28a745' : '#6c757d',
                  marginBottom: 16 
                }} />
                <p style={{ 
                  margin: 0, 
                  color: selectedFile ? '#155724' : '#6c757d',
                  fontSize: 16,
                  fontWeight: 'bold'
                }}>
                  {selectedFile ? selectedFile.name : 'Click to select file or drag and drop'}
                </p>
                <p style={{ 
                  margin: '8px 0 0 0', 
                  color: selectedFile ? '#155724' : '#6c757d',
                  fontSize: 12 
                }}>
                  Supports .xlsx, .xls, .csv files
                </p>
              </label>
            </div>

            {/* Import Button */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={resetModal}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  borderRadius: 6,
                  backgroundColor: 'white',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!selectedFile || importing}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: 6,
                  backgroundColor: selectedFile && !importing ? '#17a2b8' : '#6c757d',
                  color: 'white',
                  cursor: selectedFile && !importing ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                {importing ? `Importing... ${uploadProgress.percentage}%` : 'Import Data'}
              </button>
            </div>

            {/* Progress Bar */}
            {importing && (
              <div style={{ marginTop: 20 }}>
                <div style={{
                  backgroundColor: '#e9ecef',
                  borderRadius: 4,
                  height: 8,
                  overflow: 'hidden',
                  marginBottom: 8,
                }}>
                  <div style={{
                    backgroundColor: '#17a2b8',
                    height: '100%',
                    width: `${uploadProgress.percentage}%`,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                <p style={{
                  margin: 0,
                  fontSize: 12,
                  color: '#666',
                  textAlign: 'center',
                }}>
                  Processing {uploadProgress.current} of {uploadProgress.total} records...
                </p>
              </div>
            )}
          </>
        ) : (
          /* Import Results */
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 20 }}>
              {importResult.success ? (
                <MdCheckCircle size={64} style={{ color: '#28a745' }} />
              ) : (
                <MdError size={64} style={{ color: '#dc3545' }} />
              )}
            </div>

            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: importResult.success ? '#155724' : '#721c24',
              fontSize: 18 
            }}>
              {importResult.success ? 'Import Successful!' : 'Import Failed'}
            </h3>

            <div style={{
              backgroundColor: importResult.success ? '#d4edda' : '#f8d7da',
              border: `1px solid ${importResult.success ? '#c3e6cb' : '#f5c6cb'}`,
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
            }}>
              <p style={{ 
                margin: 0, 
                color: importResult.success ? '#155724' : '#721c24',
                fontSize: 14 
              }}>
                {importResult.message}
              </p>
              <div style={{ marginTop: 12, fontSize: 12, color: importResult.success ? '#155724' : '#721c24' }}>
                <p style={{ margin: 0 }}>✅ Records imported: {importResult.imported}</p>
                {importResult.errors > 0 && (
                  <p style={{ margin: '4px 0 0 0' }}>⚠️ Records with errors: {importResult.errors}</p>
                )}
              </div>

              {/* Error Details */}
              {importResult.errorDetails && importResult.errorDetails.length > 0 && (
                <div style={{
                  marginTop: 16,
                  maxHeight: 200,
                  overflowY: 'auto',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: 4,
                  padding: 12,
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 12, color: '#856404' }}>
                    Error Details:
                  </h4>
                  {importResult.errorDetails.slice(0, 10).map((error, index) => (
                    <div key={index} style={{
                      marginBottom: 8,
                      fontSize: 11,
                      color: '#856404',
                      borderBottom: '1px solid #ffeaa7',
                      paddingBottom: 4,
                    }}>
                      <strong>Row {error.row}:</strong> {error.error}
                      <br />
                      <span style={{ fontSize: 10, color: '#6c757d' }}>
                        Data: {JSON.stringify(error.data).substring(0, 100)}...
                      </span>
                    </div>
                  ))}
                  {importResult.errorDetails.length > 10 && (
                    <p style={{ margin: '8px 0 0 0', fontSize: 11, color: '#856404', fontStyle: 'italic' }}>
                      ... and {importResult.errorDetails.length - 10} more errors
                    </p>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={resetModal}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: 6,
                backgroundColor: '#17a2b8',
                color: 'white',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelImportModal;
